/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import axios from '../../services/axios';
import { useNotification } from '../Notification/NotificationContext';
import Forms from '../Forms';
import ErrorProcessor from '../ErrorProcessor';

export default function RequesterModal({
  show,
  onHide,
  selectedCompanyId,
  onRequesterCreated,
  prefilledEmail,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const notification = useNotification();
  const [companies, setCompanies] = useState([]);
  const [formValues, setFormValues] = useState({
    name: '',
    email: prefilledEmail || '',
    company: selectedCompanyId || '',
    telefone: '',
    celular: '',
    ramal: '',
    whatsapp_notifications: '',
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    email: Yup.string()
      .required('E-mail obrigatório.')
      .email('E-mail inválido.'),
    company: Yup.string().required('Empresa obrigatória.'),
    telefone: Yup.string()
      .matches(/^[0-9]{10}$/, 'Telefone deve conter 10 dígitos.')
      .nullable()
      .transform((value) => (value === '' ? null : value)),
    celular: Yup.string()
      .matches(/^[0-9]{11}$/, 'Celular deve conter 11 dígitos.')
      .nullable()
      .transform((value) => (value === '' ? null : value)),
    whatsapp_notifications: Yup.string()
      .matches(/^[0-9]{8,}$/, {
        message: 'WhatsApp deve conter no mínimo 8 dígitos.',
        excludeEmptyString: true,
      })
      .nullable()
      .transform((value) => (value === '' ? null : value)),
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [fieldName]: value }));
  }, []);

  const fields = [
    {
      name: 'name',
      id: 'name',
      label: 'Nome',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
    {
      name: 'email',
      id: 'email',
      label: 'E-mail',
      type: 'email',
      groupText: '@',
      onChange: (e) => handleFieldChange('email', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
    {
      name: 'company',
      id: 'company',
      label: 'Empresa',
      type: 'select',
      options: [
        { value: '', label: '--Selecione--' },
        ...companies.map((company) => ({
          value: company.id,
          label: company.razaoSocial || company.nomeEmpresa,
        })),
      ],
      onChange: (e) => handleFieldChange('company', e.target.value),
      colSize: 12,
      additionalProps: {
        disabled: !!selectedCompanyId, // Desabilita se empresa já foi selecionada
      },
    },
    {
      name: 'telefone',
      id: 'telefone',
      label: 'Telefone',
      type: 'text',
      onChange: (e) => handleFieldChange('telefone', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
    {
      name: 'celular',
      id: 'celular',
      label: 'Celular',
      type: 'text',
      onChange: (e) => handleFieldChange('celular', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
    {
      name: 'whatsapp_notifications',
      id: 'whatsapp_notifications',
      label: 'WhatsApp para notificações',
      type: 'text',
      onChange: (e) =>
        handleFieldChange('whatsapp_notifications', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
    {
      name: 'ramal',
      id: 'ramal',
      label: 'Ramal ou 0800',
      type: 'text',
      onChange: (e) => handleFieldChange('ramal', e.target.value),
      colSize: 6,
      additionalProps: {},
    },
  ];

  // Atualizar email quando prefilledEmail mudar
  useEffect(() => {
    if (prefilledEmail) {
      setFormValues((prevValues) => ({
        ...prevValues,
        email: prefilledEmail,
      }));
    }
  }, [prefilledEmail]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const payload = {
        ...formValues,
        telefone: formValues.telefone ? formValues.telefone : null,
        celular: formValues.celular ? formValues.celular : null,
        whatsapp_notifications: formValues.whatsapp_notifications
          ? formValues.whatsapp_notifications
          : null,
      };
      const { data: newRequester } = await axios.post('/requesters/', payload);
      notification('Solicitante cadastrado com sucesso', 'success');

      // Chama callback para informar que um novo solicitante foi criado
      if (onRequesterCreated) {
        onRequesterCreated(newRequester);
      }

      // Fecha o modal
      onHide();

      // Reseta o formulário
      setFormValues({
        name: '',
        email: '',
        company: selectedCompanyId || '',
        telefone: '',
        celular: '',
        ramal: '',
        whatsapp_notifications: '',
      });
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega empresas quando o modal é aberto
  useEffect(() => {
    if (show) {
      const fetchCompanies = async () => {
        try {
          const { data } = await axios.get('/customers/list');
          setCompanies(data);
        } catch (err) {
          setApiErrors(err);
        }
      };
      fetchCompanies();
    }
  }, [show]);

  // Atualiza a empresa selecionada quando selectedCompanyId muda
  useEffect(() => {
    if (selectedCompanyId) {
      setFormValues((prev) => ({ ...prev, company: selectedCompanyId }));
    }
  }, [selectedCompanyId]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Cadastrar Novo Solicitante</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorProcessor error={apiErrors} />
        <Forms
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          fields={fields}
          btnSubmit={{
            variant: 'primary',
            type: 'submit',
            text: isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cadastrando...
              </>
            ) : (
              'Cadastrar'
            ),
            disabled: isLoading,
          }}
        />
      </Modal.Body>
    </Modal>
  );
}
